import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { CALENDLY_URL } from "../data/content";
import { openCalendlyPopup } from "../integrations/calendly";

const LINKS = [
    { label: "How It Works", hash: "#how-it-works", testId: "nav-how-it-works" },
    { label: "Why Reactivation", hash: "#why-reactivation", testId: "nav-why-reactivation" },
    { label: "FAQ", to: "/faq", testId: "nav-faq" },
];

export const Nav = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [location]);

    const goHash = (hash) => {
        setOpen(false);
        if (location.pathname !== "/") {
            navigate("/" + hash);
        } else {
            document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
        }
    };

    return (
        <header
            data-testid="main-nav"
            className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-500 ${
                scrolled
                    ? "border-linen bg-cream/90 backdrop-blur-md"
                    : "border-transparent bg-cream/60 backdrop-blur-sm"
            }`}
        >
            <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
                <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
                    <img src="/logo.png" alt="Summercrest AI" className="h-7 w-7 object-contain" />
                    <span className="font-serif text-lg uppercase tracking-[0.08em] text-ink">
                        Summercrest <span className="font-mono text-[11px] tracking-[0.2em] text-gold">AI</span>
                    </span>
                </Link>

                <nav className="hidden items-center gap-9 lg:flex">
                    {LINKS.map((l) =>
                        l.to ? (
                            <Link
                                key={l.label}
                                to={l.to}
                                data-testid={l.testId}
                                className="font-sans text-[13px] uppercase tracking-[0.14em] text-mute transition-colors duration-300 hover:text-ink"
                            >
                                {l.label}
                            </Link>
                        ) : (
                            <button
                                key={l.label}
                                onClick={() => goHash(l.hash)}
                                data-testid={l.testId}
                                className="font-sans text-[13px] uppercase tracking-[0.14em] text-mute transition-colors duration-300 hover:text-ink"
                            >
                                {l.label}
                            </button>
                        )
                    )}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        data-testid="nav-book-call-btn"
                        href={CALENDLY_URL}
                        onClick={(e) => { e.preventDefault(); openCalendlyPopup(); }}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-arrow group hidden items-center gap-2.5 rounded-full bg-ink px-5 py-2.5 font-sans text-[11px] font-medium uppercase tracking-[0.16em] text-cream transition-colors duration-300 hover:bg-charcoal sm:inline-flex"
                    >
                        Book a Reactivation Call
                        <ArrowRight className="btn-arrow-icon h-3.5 w-3.5 text-gold" strokeWidth={2} />
                    </a>
                    <button
                        data-testid="nav-mobile-menu-btn"
                        onClick={() => setOpen(!open)}
                        aria-label="Toggle menu"
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-linen bg-ivory lg:hidden"
                    >
                        {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                    </button>
                </div>
            </div>

            <div
                className={`overflow-hidden border-t border-linen bg-cream/95 backdrop-blur-md transition-all duration-500 lg:hidden ${
                    open ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                }`}
                data-testid="nav-mobile-menu"
            >
                <div className="flex flex-col gap-1 px-6 py-5">
                    {LINKS.map((l) =>
                        l.to ? (
                            <Link
                                key={l.label}
                                to={l.to}
                                data-testid={`mobile-${l.testId}`}
                                className="border-b border-linen/60 py-3.5 font-sans text-sm uppercase tracking-[0.16em] text-ink"
                            >
                                {l.label}
                            </Link>
                        ) : (
                            <button
                                key={l.label}
                                onClick={() => goHash(l.hash)}
                                data-testid={`mobile-${l.testId}`}
                                className="border-b border-linen/60 py-3.5 text-left font-sans text-sm uppercase tracking-[0.16em] text-ink"
                            >
                                {l.label}
                            </button>
                        )
                    )}
                    <a
                        data-testid="mobile-nav-book-call-btn"
                        href={CALENDLY_URL}
                        onClick={(e) => { e.preventDefault(); openCalendlyPopup(); }}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 inline-flex items-center justify-center gap-2.5 rounded-full bg-ink px-5 py-3.5 font-sans text-[12px] font-medium uppercase tracking-[0.16em] text-cream"
                    >
                        Book a Reactivation Call
                        <ArrowRight className="h-3.5 w-3.5 text-gold" strokeWidth={2} />
                    </a>
                </div>
            </div>
        </header>
    );
};
